import React, { useState, useEffect } from 'react'
import { formatCurrency } from '../../utils/formatters'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
  Checkbox,
  Paper,
  FormControlLabel,
  MenuItem,
} from '@mui/material'
import { Info as InfoIcon } from '@mui/icons-material'
import { BANK_NAMES } from './banks'
import type { BillPaymentDetail, ChequeDetails } from '../../types/cheque'

interface BillPaymentData {
  id: string
  customer_name: string
  invoice_value: number
}

interface PaymentDetailsDialogProps {
  open: boolean
  onClose: () => void
  onSave: (paymentDetails: Map<string, BillPaymentDetail>) => void
  bills: BillPaymentData[]
  initialPaymentDetails?: Map<string, BillPaymentDetail>
}

const roundMoney = (value: number): number => {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

const PaymentDetailsDialog: React.FC<PaymentDetailsDialogProps> = ({
  open,
  onClose,
  onSave,
  bills,
  initialPaymentDetails,
}) => {
  const [paymentDetails, setPaymentDetails] = useState<Map<string, BillPaymentDetail>>(new Map())
  const [selectedBills, setSelectedBills] = useState<Set<string>>(new Set())
  const [chequeError, setChequeError] = useState('')

  // Initialize payment details when dialog opens or bills change
  useEffect(() => {
    if (open && bills.length > 0) {
      const newPaymentDetails = new Map<string, BillPaymentDetail>()
      const newSelectedBills = new Set<string>()
      
      bills.forEach((bill) => {
        if (initialPaymentDetails?.has(bill.id)) {
          // Use existing payment details if available
          const payment = initialPaymentDetails.get(bill.id)!
          newPaymentDetails.set(bill.id, payment)
          
          // Only check the checkbox if bill is fully cash (no credit)
          // Checked = full cash, Unchecked = any credit (full or partial)
          // Use roundMoney for comparison to handle floating point precision
          if (payment.credit_given === 0 && Math.abs(payment.cash_collected - bill.invoice_value) < 0.01) {
            newSelectedBills.add(bill.id)
          }
        } else {
          // Default: full cash, no credit
          newPaymentDetails.set(bill.id, {
            cash_collected: bill.invoice_value,
            credit_given: 0,
          })
          newSelectedBills.add(bill.id)
        }
      })
      
      setPaymentDetails(newPaymentDetails)
      setSelectedBills(newSelectedBills)
      setChequeError('')
    }
  }, [open, bills, initialPaymentDetails])

  const handleCashCollectedChange = (billId: string, value: number) => {
    const bill = bills.find(b => b.id === billId)
    if (!bill) return

    // Ensure cash collected doesn't exceed sales value
    const cashCollected = Math.max(0, Math.min(value, bill.invoice_value))
    const creditGiven = roundMoney(bill.invoice_value - cashCollected)

    setPaymentDetails(prev => {
      const newMap = new Map(prev)
      newMap.set(billId, {
        cash_collected: cashCollected,
        credit_given: creditGiven,
        ...(creditGiven > 0 && prev.get(billId)?.cheque ? { cheque: prev.get(billId)?.cheque } : {}),
      })
      return newMap
    })
  }

  const handleSelectAll = (checked: boolean) => {
    const newPaymentDetails = new Map<string, BillPaymentDetail>()
    const newSelectedBills = new Set<string>()
    
    bills.forEach(bill => {
      if (checked) {
        newSelectedBills.add(bill.id)
        // Selected: full cash, no credit
        newPaymentDetails.set(bill.id, {
          cash_collected: bill.invoice_value,
          credit_given: 0,
        })
      } else {
        // Unselected: full credit, no cash
        newPaymentDetails.set(bill.id, {
          cash_collected: 0,
          credit_given: bill.invoice_value,
        })
      }
    })
    
    setSelectedBills(newSelectedBills)
    setPaymentDetails(newPaymentDetails)
    setChequeError('')
  }

  const handleSelectBill = (billId: string, checked: boolean) => {
    const bill = bills.find(b => b.id === billId)
    if (!bill) return
    
    // Update selected bills
    setSelectedBills(prev => {
      const newSet = new Set(prev)
      if (checked) {
        newSet.add(billId)
      } else {
        newSet.delete(billId)
      }
      return newSet
    })
    
    // Update payment details based on checkbox state
    // Checked = cash, Unchecked = credit
    setPaymentDetails(prev => {
      const newMap = new Map(prev)
      if (checked) {
        // Bill is selected: full cash, no credit
        newMap.set(billId, {
          cash_collected: bill.invoice_value,
          credit_given: 0,
        })
      } else {
        // Bill is unselected: full credit, no cash
        newMap.set(billId, {
          cash_collected: 0,
          credit_given: bill.invoice_value,
        })
      }
      return newMap
    })
    setChequeError('')
  }

  const updateCheque = (billId: string, changes: Partial<ChequeDetails> | null) => {
    setPaymentDetails(prev => {
      const next = new Map(prev)
      const payment = next.get(billId)
      if (!payment || payment.credit_given <= 0) return prev
      if (changes === null) {
        next.set(billId, { ...payment, cheque: undefined })
      } else {
        next.set(billId, {
          ...payment,
          cheque: {
            amount: payment.credit_given,
            number: '',
            beneficiary_name: '',
            bank_name: '',
            branch_name: '',
            cheque_date: '',
            comments: '',
            ...payment.cheque,
            ...changes,
          },
        })
      }
      return next
    })
    setChequeError('')
  }

  const handleSave = () => {
    for (const [billId, payment] of paymentDetails) {
      const cheque = payment.cheque
      if (!cheque) continue
      const store = bills.find(bill => bill.id === billId)?.customer_name || 'Store'
      if (!Number.isFinite(cheque.amount) || cheque.amount <= 0 || cheque.amount > payment.credit_given + 0.001 ||
          !cheque.number.trim() || !cheque.beneficiary_name.trim() || !cheque.bank_name ||
          !cheque.branch_name.trim() || !/^\d{4}-\d{2}-\d{2}$/.test(cheque.cheque_date)) {
        setChequeError(`Complete the cheque details for ${store}. Cheque amount must be within its credit amount.`)
        return
      }
    }
    onSave(paymentDetails)
    onClose()
  }

  // Calculate totals
  const totals = bills.reduce(
    (acc, bill) => {
      const payment = paymentDetails.get(bill.id)
      if (payment) {
        acc.totalSales += bill.invoice_value
        acc.totalCashCollected += payment.cash_collected
        acc.totalCreditGiven += payment.credit_given
      }
      return acc
    },
    { totalSales: 0, totalCashCollected: 0, totalCreditGiven: 0 }
  )

  // Round totals
  totals.totalSales = roundMoney(totals.totalSales)
  totals.totalCashCollected = roundMoney(totals.totalCashCollected)
  totals.totalCreditGiven = roundMoney(totals.totalCreditGiven)

  const allSelected = bills.length > 0 && selectedBills.size === bills.length
  const someSelected = selectedBills.size > 0 && selectedBills.size < bills.length

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">Payment Details</Typography>
          <InfoIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        </Box>
      </DialogTitle>
      <DialogContent>
        <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.light' }}>
                <TableCell sx={{ py: 1, px: 1, fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                  Store Name
                </TableCell>
                <TableCell align="right" sx={{ py: 1, px: 1, fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                  Sales Value
                </TableCell>
                <TableCell sx={{ py: 1, px: 1, fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    size="small"
                    sx={{ 
                      color: 'white',
                      '&.Mui-checked': { color: 'white' },
                      '&.MuiCheckbox-indeterminate': { color: 'white' },
                      py: 0
                    }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ py: 1, px: 1, fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                  Cash Collected
                </TableCell>
                <TableCell align="right" sx={{ py: 1, px: 1, fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                  Credit Given
                </TableCell>
                <TableCell sx={{ py: 1, px: 1, fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                  Cheque
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bills.map((bill) => {
                const payment = paymentDetails.get(bill.id) || { cash_collected: bill.invoice_value, credit_given: 0 }
                const isSelected = selectedBills.has(bill.id)

                return (
                  <React.Fragment key={bill.id}>
                  <TableRow hover>
                    <TableCell sx={{ py: 1, px: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {bill.customer_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        [{bill.id.slice(-8)}]
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1, px: 1 }}>
                      <Typography variant="body2">
                        {formatCurrency(bill.invoice_value)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleSelectBill(bill.id, e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1, px: 1 }}>
                      <TextField
                        type="number"
                        value={payment.cash_collected}
                        onChange={(e) => handleCashCollectedChange(bill.id, Number(e.target.value))}
                        size="small"
                        inputProps={{
                          min: 0,
                          max: bill.invoice_value,
                          step: 0.01,
                          style: { 
                            textAlign: 'right',
                            MozAppearance: 'textfield',
                            WebkitAppearance: 'none',
                            margin: 0
                          }
                        }}
                        sx={{
                          width: '120px',
                          '& input[type=number]': {
                            MozAppearance: 'textfield',
                          },
                          '& input[type=number]::-webkit-outer-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                          '& input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1, px: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(payment.credit_given)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>
                      <FormControlLabel
                        control={<Checkbox checked={!!payment.cheque} disabled={payment.credit_given <= 0}
                          onChange={(e) => updateCheque(bill.id, e.target.checked ? {} : null)} size="small" />}
                        label="Cheque"
                      />
                    </TableCell>
                  </TableRow>
                  {payment.cheque && payment.credit_given > 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ bgcolor: 'grey.50' }}>
                        <Box display="flex" flexWrap="wrap" gap={1} py={1}>
                          <TextField label="Cheque Amount" type="number" required size="small" sx={{ width: 150 }}
                            value={payment.cheque.amount} inputProps={{ min: 0.01, max: payment.credit_given, step: 0.01 }}
                            onChange={(e) => updateCheque(bill.id, { amount: Number(e.target.value) })} />
                          <TextField label="Cheque Number" required size="small" sx={{ width: 160 }}
                            value={payment.cheque.number} onChange={(e) => updateCheque(bill.id, { number: e.target.value })} />
                          <TextField label="Beneficiary Name" required size="small" sx={{ width: 180 }}
                            value={payment.cheque.beneficiary_name} onChange={(e) => updateCheque(bill.id, { beneficiary_name: e.target.value })} />
                          <TextField select label="Bank Name" required size="small" sx={{ width: 220 }}
                            value={payment.cheque.bank_name} onChange={(e) => updateCheque(bill.id, { bank_name: e.target.value })}>
                            {BANK_NAMES.map(bank => <MenuItem key={bank} value={bank}>{bank}</MenuItem>)}
                          </TextField>
                          <TextField label="Branch Name" required size="small" sx={{ width: 170 }}
                            value={payment.cheque.branch_name} onChange={(e) => updateCheque(bill.id, { branch_name: e.target.value })} />
                          <TextField label="Cheque Date" type="date" required size="small" sx={{ width: 170 }}
                            value={payment.cheque.cheque_date} InputLabelProps={{ shrink: true }}
                            onChange={(e) => updateCheque(bill.id, { cheque_date: e.target.value })} />
                          <TextField label="Comments" size="small" sx={{ minWidth: 180, flexGrow: 1 }}
                            value={payment.cheque.comments || ''} onChange={(e) => updateCheque(bill.id, { comments: e.target.value })} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                )
              })}
              {/* Totals Row */}
              <TableRow sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>
                <TableCell sx={{ py: 1, px: 1, fontWeight: 'bold' }}>
                  <Typography variant="body2" fontWeight="bold">
                    Total
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ py: 1, px: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(totals.totalSales)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1, px: 1 }}></TableCell>
                <TableCell align="right" sx={{ py: 1, px: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(totals.totalCashCollected)}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ py: 1, px: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(totals.totalCreditGiven)}
                  </Typography>
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      {chequeError && <Typography color="error" sx={{ px: 3 }}>{chequeError}</Typography>}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Payment Details
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PaymentDetailsDialog

